import { makeAutoObservable } from 'mobx';
import type RootStore from '../../stores/RootStore';

/**
 * ProductViewStore - Manages UI state for product views
 *
 * Per CLAUDE.md standards:
 * - NEVER stores API entities directly
 * - Only manages UI state (selections, filters, view modes)
 * - All entity data accessed via computed values from ServiceStores
 * - Route ViewStores are plain classes with makeAutoObservable (NOT extending ViewStore)
 */
class ProductViewStore {
    // UI State Only
    selectedProductId: string | null = null;
    selectedCategoryId: string | null = null;
    selectedVariantId: string | null = null;
    selectedBOMId: string | null = null;

    // View State
    selectedTab = 'overview';
    viewMode: 'grid' | 'list' = 'list';
    searchQuery = '';

    // Filters (UI state only)
    filters = {
        category: null as string | null,
        department: null as string | null,
        productType: '',
        active: true,
        inStock: false,
        priceRange: {
            min: null as number | null,
            max: null as number | null
        }
    };

    // Pagination (UI state)
    pagination = {
        page: 1,
        limit: 25,
        sortBy: 'name',
        sortOrder: 'asc' as 'asc' | 'desc'
    };

    // Dialog States (UI only)
    dialogs = {
        productForm: false,
        variantForm: false,
        bomForm: false,
        pricingForm: false,
        inventoryForm: false,
        movementDialog: false
    };

    constructor(private rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false
        });
    }

    // ========================================
    // Computed values - Reference ServiceStore entities
    // Per CLAUDE.md: Never store entities, only compute from ServiceStores
    // ========================================

    get products() {
        // Access products from the ServiceStore (NOT storing them here)
        const productsStore = this.rootStore.productsStore as any;
        return productsStore?.entities || [];
    }

    get selectedProduct() {
        if (!this.selectedProductId) return null;
        // Use .entities.find() per CLAUDE.md (not getById)
        return this.products.find((p: any) => p.id === this.selectedProductId);
    }

    get categories() {
        // Access from ServiceStore if available
        const categoriesStore = this.rootStore.productCategoriesStore as any;
        return categoriesStore?.entities || [];
    }

    get selectedCategory() {
        if (!this.selectedCategoryId) return null;
        return this.categories.find((c: any) => c.id === this.selectedCategoryId);
    }

    get variants() {
        // Get variants for selected product from ServiceStore
        const variantsStore = this.rootStore.productVariantsStore as any;
        const allVariants = variantsStore?.entities || [];

        if (!this.selectedProductId) return [];
        return allVariants.filter((v: any) => v.productId === this.selectedProductId);
    }

    get selectedVariant() {
        if (!this.selectedVariantId) return null;
        return this.variants.find((v: any) => v.id === this.selectedVariantId);
    }

    get billOfMaterials() {
        // Get BOMs from ServiceStore
        const bomStore = this.rootStore.billOfMaterialsStore as any;
        const allBoms = bomStore?.entities || [];

        if (!this.selectedProductId) return [];
        return allBoms.filter((bom: any) => bom.productId === this.selectedProductId);
    }

    get selectedBOM() {
        if (!this.selectedBOMId) return null;
        return this.billOfMaterials.find((bom: any) => bom.id === this.selectedBOMId);
    }

    get bomItems() {
        // Get BOM items for selected BOM
        const bomItemsStore = this.rootStore.bomItemsStore as any;
        const allItems = bomItemsStore?.entities || [];

        if (!this.selectedBOMId) return [];
        return allItems.filter((item: any) => item.billOfMaterialsId === this.selectedBOMId);
    }

    get filteredProducts() {
        let filtered = this.products;

        // Apply search filter
        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter((product: any) =>
                product.name?.toLowerCase().includes(query) ||
                product.productCode?.toLowerCase().includes(query) ||
                product.description?.toLowerCase().includes(query)
            );
        }

        // Apply category filter
        if (this.filters.category) {
            filtered = filtered.filter((product: any) =>
                product.categoryId === this.filters.category
            );
        }

        // Apply department filter
        if (this.filters.department) {
            filtered = filtered.filter((product: any) =>
                product.departmentId === this.filters.department
            );
        }

        // Apply product type filter
        if (this.filters.productType) {
            filtered = filtered.filter((product: any) =>
                product.productType === this.filters.productType
            );
        }

        // Apply active filter
        if (this.filters.active !== null) {
            filtered = filtered.filter((product: any) =>
                product.active === this.filters.active
            );
        }

        // Apply in-stock filter
        if (this.filters.inStock) {
            filtered = filtered.filter((product: any) =>
                (product.quantityOnHand || 0) > 0
            );
        }

        // Apply price range filter
        if (this.filters.priceRange.min !== null) {
            filtered = filtered.filter((product: any) =>
                (product.price || 0) >= this.filters.priceRange.min!
            );
        }
        if (this.filters.priceRange.max !== null) {
            filtered = filtered.filter((product: any) =>
                (product.price || 0) <= this.filters.priceRange.max!
            );
        }

        return filtered;
    }

    get sortedProducts() {
        const sorted = [...this.filteredProducts];
        const { sortBy, sortOrder } = this.pagination;

        sorted.sort((a: any, b: any) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal?.toLowerCase() || '';
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        return sorted;
    }

    get paginatedProducts() {
        const { page, limit } = this.pagination;
        const start = (page - 1) * limit;
        const end = start + limit;
        return this.sortedProducts.slice(start, end);
    }

    get totalPages() {
        return Math.ceil(this.filteredProducts.length / this.pagination.limit);
    }

    get productsByDepartment() {
        // Group products by department
        const grouped = new Map<string, any[]>();

        this.products.forEach((product: any) => {
            const deptId = product.departmentId || 'uncategorized';
            if (!grouped.has(deptId)) {
                grouped.set(deptId, []);
            }
            grouped.get(deptId)!.push(product);
        });

        return grouped;
    }

    get categoryTree() {
        // Build category tree structure from flat categories
        const tree: any[] = [];
        const map = new Map();

        this.categories.forEach((cat: any) => {
            map.set(cat.id, { ...cat, children: [] });
        });

        map.forEach((cat) => {
            if (cat.parentId) {
                const parent = map.get(cat.parentId);
                if (parent) {
                    parent.children.push(cat);
                }
            } else {
                tree.push(cat);
            }
        });

        return tree;
    }

    get hasFilters() {
        return !!(
            this.searchQuery ||
            this.filters.category ||
            this.filters.department ||
            this.filters.productType ||
            this.filters.inStock ||
            this.filters.priceRange.min !== null ||
            this.filters.priceRange.max !== null
        );
    }

    // ========================================
    // UI Actions - Only manage UI state
    // Per CLAUDE.md: ViewStores only manage UI state
    // ========================================

    selectProduct(productId: string | null) {
        this.selectedProductId = productId;
        this.selectedVariantId = null;
        this.selectedBOMId = null;

        // Load related data if product selected
        if (productId) {
            this.loadProductDetails(productId);
        }
    }

    selectCategory(categoryId: string | null) {
        this.selectedCategoryId = categoryId;
        this.filters.category = categoryId;
        this.pagination.page = 1;
    }

    selectVariant(variantId: string | null) {
        this.selectedVariantId = variantId;
    }

    selectBOM(bomId: string | null) {
        this.selectedBOMId = bomId;

        // Load BOM items if BOM selected
        if (bomId) {
            this.loadBOMItems(bomId);
        }
    }

    setSelectedTab(tab: string) {
        this.selectedTab = tab;
    }

    setViewMode(mode: 'grid' | 'list') {
        this.viewMode = mode;
    }

    setSearchQuery(query: string) {
        this.searchQuery = query;
        this.pagination.page = 1;
    }

    setFilter(filterKey: keyof typeof this.filters, value: any) {
        (this.filters as any)[filterKey] = value;
        this.pagination.page = 1;
    }

    setPriceRange(min: number | null, max: number | null) {
        this.filters.priceRange.min = min;
        this.filters.priceRange.max = max;
        this.pagination.page = 1;
    }

    setPage(page: number) {
        this.pagination.page = page;
    }

    setPageSize(limit: number) {
        this.pagination.limit = limit;
        this.pagination.page = 1;
    }

    setSortBy(field: string, order?: 'asc' | 'desc') {
        this.pagination.sortBy = field;
        if (order) {
            this.pagination.sortOrder = order;
        } else {
            // Toggle order if same field
            if (this.pagination.sortBy === field) {
                this.pagination.sortOrder = this.pagination.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                this.pagination.sortOrder = 'asc';
            }
        }
    }

    clearFilters() {
        this.filters = {
            category: null,
            department: null,
            productType: '',
            active: true,
            inStock: false,
            priceRange: {
                min: null,
                max: null
            }
        };
        this.searchQuery = '';
        this.pagination.page = 1;
    }

    resetSelection() {
        this.selectedProductId = null;
        this.selectedCategoryId = null;
        this.selectedVariantId = null;
        this.selectedBOMId = null;
        this.selectedTab = 'overview';
    }

    // Dialog Management (UI state only)
    openDialog(dialogName: keyof typeof this.dialogs) {
        this.dialogs[dialogName] = true;
    }

    closeDialog(dialogName: keyof typeof this.dialogs) {
        this.dialogs[dialogName] = false;
    }

    // ========================================
    // Service Store Actions - Delegate to ServiceStore
    // Per CLAUDE.md: Load data in route hooks, not components
    // ========================================

    async loadProducts(query?: any) {
        const productsStore = this.rootStore.productsStore as any;
        if (productsStore?.find) {
            await productsStore.find({
                query: query || {
                    $limit: 1000
                }
            });
        }
    }

    async loadProductDetails(productId: string) {
        const productsStore = this.rootStore.productsStore as any;
        if (productsStore?.get) {
            await productsStore.get(productId);
        }

        // Load related data in parallel
        await Promise.all([
            this.loadVariants(productId),
            this.loadBillOfMaterials(productId),
            this.loadPricing(productId),
            this.loadInventory(productId)
        ]);
    }

    async loadVariants(productId: string) {
        const variantsStore = this.rootStore.productVariantsStore as any;
        if (variantsStore?.find) {
            await variantsStore.find({
                query: {
                    productId,
                    $limit: 100
                }
            });
        }
    }

    async loadBillOfMaterials(productId: string) {
        const bomStore = this.rootStore.billOfMaterialsStore as any;
        if (bomStore?.find) {
            await bomStore.find({
                query: {
                    productId,
                    $limit: 100
                }
            });
        }
    }

    async loadBOMItems(bomId: string) {
        const bomItemsStore = this.rootStore.bomItemsStore as any;
        if (bomItemsStore?.find) {
            await bomItemsStore.find({
                query: {
                    billOfMaterialsId: bomId,
                    $limit: 500
                }
            });
        }
    }

    async loadPricing(productId: string) {
        const pricingStore = this.rootStore.productPricingStore as any;
        if (pricingStore?.find) {
            await pricingStore.find({
                query: {
                    productId,
                    $limit: 100
                }
            });
        }
    }

    async loadInventory(productId: string) {
        const inventoryStore = this.rootStore.productInventoryStore as any;
        if (inventoryStore?.find) {
            await inventoryStore.find({
                query: {
                    productId,
                    $limit: 100
                }
            });
        }
    }

    async loadCategories() {
        const categoriesStore = this.rootStore.productCategoriesStore as any;
        if (categoriesStore?.find) {
            await categoriesStore.find({
                query: {
                    $limit: 500
                }
            });
        }
    }

    // Initialize - Load initial data
    // Per CLAUDE.md: This should be called from route beforeEnter hook
    async initialize() {
        await Promise.all([
            this.loadProducts(),
            this.loadCategories()
        ]);
    }

    // Cleanup
    dispose() {
        this.resetSelection();
        this.clearFilters();

        // Close all dialogs
        Object.keys(this.dialogs).forEach(key => {
            (this.dialogs as any)[key] = false;
        });
    }
}

export default ProductViewStore;